<script setup lang="ts">
import { ref, onMounted } from 'vue';
import sanitizeHtml from 'sanitize-html';

interface OverviewItem {
  title: string;
  text_editor: boolean;
  text: string;
  article: string;
  open?: boolean;
}

interface ListItem {
  sub_heading: string;
  ancor_id: string;
  overviewList: OverviewItem[];
}

const props = defineProps<{
  section: {
    heading: string;
    text: string;
    button: {
      url: string;
      title: string;
      target: string;
    };
    decoration_image: {
      url: string;
      width?: number;
      height?: number;
      alt?: string;
      name?: string;
    };
    list_group: ListItem[];
  };
}>();

const openStates = ref(new Map<string, boolean>());

onMounted(() => {
  props.section.list_group.forEach((group) => {
    group.overviewList.forEach((item) => {
      openStates.value.set(item.title, !!item.open);
    });
  });
});

const toggleDropdown = (title: string) => {
  openStates.value.forEach((_, key) => {
    openStates.value.set(
      key,
      key === title ? !openStates.value.get(key) : false
    );
  });

  if (openStates.value.get(title)) {
    const element = document.querySelector(`[data-title="${title}"]`);
    if (element) {
      setTimeout(() => {
        const headerHeight =
          document.querySelector('#header')?.clientHeight || 0;
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - headerHeight,
          behavior: 'smooth',
        });
      }, 500);
    }
  }
};
</script>

<template>
  <section class="relative z-10 bg-grad-creamgreen pb-16 pt-15 md:py-28">
    <div class="container md:grid md:grid-cols-2 lg:pt-4">
      <div class="mb-8 md:mb-0 md:pr-4">
        <div class="top-24 md:sticky">
          <h2 class="mb-1 md:mb-4">{{ section.heading }}</h2>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div
            v-if="section.text"
            class="mb-6 text-xl xl:pr-32"
            v-html="sanitizeHtml(section.text)"
          />
          <ButtonLink
            v-if="section.button"
            v-bind="section.button"
            icon="arrow-r"
          />
        </div>
        <Image
          :data="section.decoration_image"
          class="bootm-0 absolute left-0 -z-10 hidden md:block"
        />
      </div>
      <div class="xl:pl-3">
        <template
          v-for="(groupItem, groupIndex) in section.list_group"
          :key="`${groupItem.sub_heading}-${groupIndex}`"
        >
          <h3
            v-if="groupItem.sub_heading"
            :id="groupItem.ancor_id"
            class="h5 mb-5 scroll-mt-30 xl:leading-lg"
          >
            {{ groupItem.sub_heading }}
          </h3>
          <ul
            class="mb-14 border-t border-dotted border-current md:mb-19 md:last:mb-4"
          >
            <template
              v-for="(overviewItem, index) in groupItem.overviewList"
              :key="`${overviewItem.title}-${index}`"
            >
              <li
                v-if="
                  overviewItem.title ||
                  overviewItem.article ||
                  overviewItem.text
                "
                class="border-b border-dotted border-current py-7"
                :data-title="overviewItem.title"
              >
                <button
                  v-if="overviewItem.title"
                  type="button"
                  class="title relative block w-full cursor-pointer pr-8 text-start hover:text-blue md:-mb-1 md:mt-0.5"
                  @click="toggleDropdown(overviewItem.title)"
                >
                  <strong class="font-normal">{{ overviewItem.title }}</strong>
                  <svg
                    class="absolute right-0 top-3 h-3 w-3 transition-all duration-300"
                    :class="{
                      '-rotate-180': !openStates.get(overviewItem.title),
                    }"
                  >
                    <use href="/icons/icons.svg#arrow" />
                  </svg>
                </button>
                <div
                  class="slide grid transition-all duration-500"
                  :class="{
                    'visible mt-4 grid-rows-[1fr]': openStates.get(
                      overviewItem.title
                    ),
                    'invisible grid-rows-[0fr]': !openStates.get(
                      overviewItem.title
                    ),
                  }"
                >
                  <template v-if="overviewItem.text_editor">
                    <div
                      v-if="overviewItem.article"
                      class="typography typography-base -ml-1 overflow-hidden pl-1 xl:pr-2.5"
                      v-html="
                        sanitizeHtml(overviewItem.article, {
                          allowedAttributes: false,
                          allowedTags: false,
                          allowVulnerableTags: true,
                          transformTags: {
                            script: () => ({
                              tagName: 'noscript',
                              attribs: {},
                            }),
                          },
                        })
                      "
                    />
                  </template>
                  <template v-else>
                    <div
                      v-if="overviewItem.text"
                      class="overflow-hidden xl:pr-8"
                      v-html="sanitizeHtml(overviewItem.text)"
                    />
                  </template>
                </div>
              </li>
            </template>
          </ul>
        </template>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.slide {
  a {
    @apply underline decoration-dotted decoration-1 underline-offset-8 hover:no-underline;
  }
}
</style>
